/**
 * Retrieve Brandfetch Tool
 *
 * Downloads and processes specific logos or brand colors from Brandfetch.
 * Performs:
 * - Asset download from Brandfetch
 * - Image processing and dimension detection
 * - Asset storage with deduplication via content hash
 * - Description generation
 */
import { ToolDescription, safeParseToolParams } from "../../../../base/toolDescription";
import { ToolBase } from "../../../../base/toolBase";
import { generateId } from "../../../../../utils/idGenerator";
import { ContextResolver } from "../../../../../core/contextResolver";
import { resizeImageForVision } from "../../utils/imageResize";
import { simpleHash, findExistingAsset, registerContentHash } from "../../utils/deduplicationCache";
import { extractPaletteFromImage } from "../../utils/paletteExtraction";
import { RetrieveBrandfetchParamsSchema, } from "./retrieveBrandfetch.schema";
import { BRANDFETCH_API_KEY, BRANDFETCH_BRAND_URL, BRANDFETCH_LOGO_URL, BRANDFETCH_CUSTOMER_ID } from "../../config";
// ═══════════════════════════════════════════════════════════════════════════
// IMAGE DIMENSION DETECTION
// ═══════════════════════════════════════════════════════════════════════════
/**
 * Get image dimensions from base64 data (supports PNG, JPEG, WEBP).
 */
function getImageDimensions(base64) {
    try {
        const binary = atob(base64);
        const bytes = new Uint8Array(binary.length);
        for (let i = 0; i < binary.length; i++) {
            bytes[i] = binary.charCodeAt(i);
        }
        // PNG: 89 50 4E 47 (‰PNG)
        if (bytes[0] === 0x89 && bytes[1] === 0x50 && bytes[2] === 0x4E && bytes[3] === 0x47) {
            const width = (bytes[16] << 24) | (bytes[17] << 16) | (bytes[18] << 8) | bytes[19];
            const height = (bytes[20] << 24) | (bytes[21] << 16) | (bytes[22] << 8) | bytes[23];
            return { width, height };
        }
        // JPEG: FF D8 FF
        if (bytes[0] === 0xFF && bytes[1] === 0xD8 && bytes[2] === 0xFF) {
            let offset = 2;
            while (offset < bytes.length) {
                if (bytes[offset] !== 0xFF)
                    break;
                const marker = bytes[offset + 1];
                offset += 2;
                // SOF markers (Start Of Frame) contain dimensions
                if ((marker >= 0xC0 && marker <= 0xC3) || (marker >= 0xC5 && marker <= 0xC7) ||
                    (marker >= 0xC9 && marker <= 0xCB) || (marker >= 0xCD && marker <= 0xCF)) {
                    const height = (bytes[offset + 3] << 8) | bytes[offset + 4];
                    const width = (bytes[offset + 5] << 8) | bytes[offset + 6];
                    return { width, height };
                }
                // Skip segment
                const segmentLength = (bytes[offset] << 8) | bytes[offset + 1];
                offset += segmentLength;
            }
        }
        // WEBP: RIFF ... WEBP
        if (bytes[0] === 0x52 && bytes[1] === 0x49 && bytes[2] === 0x46 && bytes[3] === 0x46 &&
            bytes[8] === 0x57 && bytes[9] === 0x45 && bytes[10] === 0x42 && bytes[11] === 0x50) {
            // VP8 (lossy)
            if (bytes[12] === 0x56 && bytes[13] === 0x50 && bytes[14] === 0x38 && bytes[15] === 0x20) {
                const width = ((bytes[26] | (bytes[27] << 8)) & 0x3fff);
                const height = ((bytes[28] | (bytes[29] << 8)) & 0x3fff);
                return { width, height };
            }
            // VP8L (lossless)
            if (bytes[12] === 0x56 && bytes[13] === 0x50 && bytes[14] === 0x38 && bytes[15] === 0x4C) {
                const bits = (bytes[21] << 24) | (bytes[22] << 16) | (bytes[23] << 8) | bytes[24];
                const width = ((bits & 0x3FFF) + 1);
                const height = (((bits >> 14) & 0x3FFF) + 1);
                return { width, height };
            }
            // VP8X (extended)
            if (bytes[12] === 0x56 && bytes[13] === 0x50 && bytes[14] === 0x38 && bytes[15] === 0x58) {
                const width = 1 + (bytes[24] | (bytes[25] << 8) | (bytes[26] << 16));
                const height = 1 + (bytes[27] | (bytes[28] << 8) | (bytes[29] << 16));
                return { width, height };
            }
        }
        return null;
    }
    catch {
        return null;
    }
}
/**
 * Get SVG dimensions from content.
 */
function getSvgDimensions(svgContent) {
    try {
        const widthMatch = svgContent.match(/width=["'](\d+(?:\.\d+)?)/);
        const heightMatch = svgContent.match(/height=["'](\d+(?:\.\d+)?)/);
        if (widthMatch && heightMatch) {
            return {
                width: Math.round(parseFloat(widthMatch[1])),
                height: Math.round(parseFloat(heightMatch[1])),
            };
        }
        const viewBoxMatch = svgContent.match(/viewBox=["'][\d.]+\s+[\d.]+\s+([\d.]+)\s+([\d.]+)/);
        if (viewBoxMatch) {
            return {
                width: Math.round(parseFloat(viewBoxMatch[1])),
                height: Math.round(parseFloat(viewBoxMatch[2])),
            };
        }
        return null;
    }
    catch {
        return null;
    }
}
/**
 * Determine shape from dimensions.
 */
function getShapeFromDimensions(width, height) {
    const aspectRatio = width / height;
    if (aspectRatio >= 0.9 && aspectRatio <= 1.1) {
        return "square";
    }
    else if (aspectRatio > 1.1) {
        return "horizontal";
    }
    else {
        return "vertical";
    }
}
// ═══════════════════════════════════════════════════════════════════════════
// BRANDFETCH API CLIENT
// ═══════════════════════════════════════════════════════════════════════════
/** Custom error for rate limiting */
class RateLimitError extends Error {
    constructor(message) {
        super(message);
        this.name = 'RateLimitError';
    }
}
/**
 * Fetch brand details from Brandfetch.
 * @throws {RateLimitError} When API rate limit is hit
 */
async function fetchBrandDetails(domain, apiKey) {
    try {
        const response = await fetch(`${BRANDFETCH_BRAND_URL}/${encodeURIComponent(domain)}`, {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${apiKey}`,
                'Accept': 'application/json',
            },
        });
        if (response.ok) {
            return await response.json();
        }
        else if (response.status === 429) {
            // Rate limit hit - throw error to stop
            console.warn('[RetrieveBrandfetch] ⚠️ Rate limit hit (429)');
            throw new RateLimitError('Brandfetch API rate limit exceeded. Please try again in a few minutes.');
        }
        return null;
    }
    catch (error) {
        // Re-throw rate limit errors
        if (error instanceof RateLimitError) {
            throw error;
        }
        console.error('[RetrieveBrandfetch] Brand fetch error:', error);
        return null;
    }
}
/**
 * Find the best logo format matching the requested variant.
 * Variant format: "light_icon", "dark_logo", "light_symbol", "icon", "logo", "symbol"
 */
function findLogoForVariant(brandData, variant) {
    const logos = brandData.logos || [];
    // Parse variant to extract theme and type
    const parts = variant.split('_');
    const requestedTheme = parts.length === 2 ? parts[0] : null; // "light" or "dark" or null
    const requestedType = parts.length === 2 ? parts[1] : variant; // "icon", "logo", or "symbol"
    for (const logo of logos) {
        const logoType = logo.type?.toLowerCase() || '';
        const logoTheme = logo.theme?.toLowerCase() || '';
        // Match type
        const typeMatches = (requestedType === 'icon' && (logoType.includes('icon') || logoType.includes('symbol'))) ||
            (requestedType === 'logo' && logoType.includes('logo')) ||
            (requestedType === 'symbol' && (logoType.includes('symbol') || logoType.includes('icon')));
        // Match theme (if specified)
        const themeMatches = !requestedTheme ||
            (requestedTheme === 'light' && (logoTheme === 'light' || logoType.includes('light'))) ||
            (requestedTheme === 'dark' && (logoTheme === 'dark' || logoType.includes('dark')));
        if (typeMatches && themeMatches) {
            const formats = logo.formats || [];
            let bestFormat = null;
            // Prefer SVG over PNG
            for (const fmt of formats) {
                if (fmt.format === 'svg') {
                    bestFormat = fmt;
                    break;
                }
                else if (fmt.format === 'png' && !bestFormat) {
                    bestFormat = fmt;
                }
            }
            if (bestFormat) {
                return {
                    url: bestFormat.src,
                    format: (bestFormat.format === 'svg' ? 'svg' : 'png'),
                    width: bestFormat.width,
                    height: bestFormat.height,
                };
            }
        }
    }
    // Fallback: return first available logo
    if (logos.length > 0 && logos[0].formats?.length > 0) {
        const fmt = logos[0].formats.find(f => f.format === 'svg') || logos[0].formats[0];
        return {
            url: fmt.src,
            format: (fmt.format === 'svg' ? 'svg' : 'png'),
            width: fmt.width,
            height: fmt.height,
        };
    }
    return null;
}
/**
 * Extract all available logo variants from brand data (deduplicated).
 * Returns list of unique variants to fetch.
 */
function extractAvailableVariants(brandData) {
    const variants = new Set();
    for (const logo of brandData.logos || []) {
        const logoType = logo.type?.toLowerCase() || '';
        const logoTheme = logo.theme?.toLowerCase() || '';
        // Determine type
        let type;
        if (logoType.includes('icon')) {
            type = 'icon';
        }
        else if (logoType.includes('symbol')) {
            type = 'symbol';
        }
        else {
            type = 'logo';
        }
        // Determine theme
        let theme = null;
        if (logoTheme === 'light' || logoType.includes('light')) {
            theme = 'light';
        }
        else if (logoTheme === 'dark' || logoType.includes('dark')) {
            theme = 'dark';
        }
        // Build variant string
        const variant = (theme ? `${theme}_${type}` : type);
        variants.add(variant);
    }
    return Array.from(variants);
}
/**
 * Build Logo API URL with parameters.
 * See: https://docs.brandfetch.com/logo-api/parameters
 *
 * Format: https://cdn.brandfetch.io/{domain}/{params}?c={customerId}
 * Example: https://cdn.brandfetch.io/apple.com/w/400/h/400/theme/dark/type/icon?c=1id9oMFwwTeeYBCszzX
 */
function buildLogoApiUrl(domain, customerId, variant, width, height) {
    // Base URL: https://cdn.brandfetch.io/{domain}
    // Domain should come from searchBrandfetchTool (e.g., "apple.com")
    let url = `${BRANDFETCH_LOGO_URL}/${encodeURIComponent(domain)}`;
    // Build path parts according to API spec
    const pathParts = [];
    // Width and height (if provided)
    if (width) {
        pathParts.push(`w/${width}`);
    }
    if (height) {
        pathParts.push(`h/${height}`);
    }
    // Parse variant to extract theme and type
    // Variants: "light_icon", "dark_logo", "light_symbol", "icon", "logo", "symbol"
    if (variant) {
        const parts = variant.split('_');
        if (parts.length === 2) {
            // Has theme: "light_icon" -> ["light", "icon"]
            const [theme, type] = parts;
            pathParts.push(`theme/${theme}`);
            pathParts.push(`type/${type}`);
        }
        else {
            // No theme, just type: "icon" -> ["icon"]
            pathParts.push(`type/${variant}`);
        }
    }
    else {
        // Default: type/icon
        pathParts.push('type/icon');
    }
    // Add path parts if any
    if (pathParts.length > 0) {
        url += '/' + pathParts.join('/');
    }
    // Add customer ID as query parameter (c={customerId})
    url += `?c=${encodeURIComponent(customerId)}`;
    return url;
}
/**
 * Download image and convert to base64.
 */
async function downloadImageAsBase64(url) {
    try {
        const response = await fetch(url);
        if (!response.ok) {
            console.warn(`[RetrieveBrandfetch] Failed to download image: ${response.status}`);
            return null;
        }
        const contentType = response.headers.get('content-type') || 'image/png';
        const arrayBuffer = await response.arrayBuffer();
        const bytes = new Uint8Array(arrayBuffer);
        let binary = '';
        for (let i = 0; i < bytes.length; i++) {
            binary += String.fromCharCode(bytes[i]);
        }
        const base64 = btoa(binary);
        let mimeType = contentType.split(';')[0].trim();
        if (url.endsWith('.svg') || mimeType.includes('svg')) {
            mimeType = 'image/svg+xml';
        }
        else if (url.endsWith('.png') || mimeType.includes('png')) {
            mimeType = 'image/png';
        }
        return { base64, mimeType };
    }
    catch (error) {
        console.error('[RetrieveBrandfetch] Download error:', error);
        return null;
    }
}
/**
 * Extract brand colors from Brandfetch data.
 */
function extractBrandColors(brandData) {
    const colors = [];
    const colorList = brandData.colors || [];
    for (let i = 0; i < Math.min(colorList.length, 5); i++) {
        const color = colorList[i];
        const hex = color.hex || color.value;
        if (hex) {
            const colorType = color.type?.toLowerCase() || (i === 0 ? 'primary' : `color_${i}`);
            colors.push({
                hex: hex.startsWith('#') ? hex : `#${hex}`,
                type: colorType,
            });
        }
    }
    return colors;
}
/**
 * Generate a description for the asset.
 */
function generateAssetDescription(companyDomain, assetType, format, shape, colors, variant, includeTone) {
    const companyName = companyDomain.split('.')[0];
    const capitalizedName = companyName.charAt(0).toUpperCase() + companyName.slice(1);
    if (assetType === 'logo') {
        const formatStr = format === 'svg' ? 'vector' : 'raster';
        const shapeStr = shape || 'rectangular';
        const hasText = variant?.includes('logo') ? 'with text' : (variant?.includes('icon') || variant?.includes('symbol')) ? 'no text' : '';
        const tone = includeTone ? (variant?.includes('dark') ? 'dark' : variant?.includes('light') ? 'light' : '') : '';
        const variantParts = [tone, hasText].filter(Boolean).join(', ');
        const variantStr = variantParts ? `, ${variantParts}` : '';
        return `${capitalizedName} logo, ${formatStr} format, ${shapeStr} orientation${variantStr}`;
    }
    else {
        const colorCount = colors?.length || 0;
        const primaryColor = colors?.[0]?.hex || '#000000';
        return `${capitalizedName} brand palette with ${colorCount} colors, primary: ${primaryColor}`;
    }
}
// ═══════════════════════════════════════════════════════════════════════════
// RETRIEVE BRANDFETCH TOOL
// ═══════════════════════════════════════════════════════════════════════════
// System prompt fallback for Keywords AI
const RETRIEVE_BRANDFETCH_SYSTEM_PROMPT = `### retrieve_brandfetch
Download logos or brand colors from Brandfetch.

**When to use:**
- After search_brandfetch found the company
- To get actual logo images for insertion
- To get brand color palettes

**Call AFTER search_brandfetch** to know which domains/variants are available.

**Parameters:**
- \`companyDomain\`: Domain from search results (e.g., "microsoft.com")
- \`assetType\`: "logo" | "brand_colors"
- \`requirementId\`: The requirement this asset fulfills
- \`logoOnly\`: true (logo-only via Logo API) | false (full brand data via Brand API)

**Logo retrieval behavior:**
- When \`assetType\` is "logo", this tool retrieves ALL available logo variants automatically.
- Do NOT pass \`logoVariant\` (ignored).

\`\`\`json
{
  "companyDomain": "microsoft.com",
  "assetType": "logo",
  "logoOnly": true,
  "requirementId": "A"
}
\`\`\`

Returns: assets[] with logo_* or color_* asset IDs for use with insertArtifacts.`;
/**
 * Tool for downloading and processing Brandfetch assets.
 */
export class RetrieveBrandfetchTool extends ToolBase {
    constructor() {
        super();
        this.name = "retrieve_brandfetch";
        // System prompt description
        this.systemPromptDescription = RETRIEVE_BRANDFETCH_SYSTEM_PROMPT;
        this.fieldsToCheck = [
            { name: 'uiDescription', type: 'string', list_status: false, order: 0 },
            { name: 'companyDomain', type: 'string', list_status: false, order: 1 },
            { name: 'assetType', type: 'string', list_status: false, order: 2 },
            { name: 'requirementId', type: 'string', list_status: false, order: 3 },
            { name: 'logoVariant', type: 'string', list_status: false, order: 4 },
        ];
        this.listsToCheck = [];
        this.description = new ToolDescription("retrieve_brandfetch", `Download and process logo or brand color assets from Brandfetch.

**Purpose:** Retrieve assets found via searchBrandfetch.

**Parameters:**
- companyDomain: Domain from search result (e.g., "microsoft.com")
- assetType: "logo" or "brand_colors"
- requirementId: The requirement this asset fulfills
- logoOnly: (optional) If true, use Logo API (fast, direct CDN). If false, use Brand API (slower, more details)

**Logo retrieval behavior:**
- When assetType is "logo", all available variants are fetched automatically.
- Do NOT pass logoVariant (ignored).

**Example:**
\`\`\`json
{
  "companyDomain": "microsoft.com",
  "assetType": "logo",
  "requirementId": "A",
  "logoOnly": true
}
\`\`\`

**Returns:**
- assets: Array of retrieved assets (logo_* or color_*)
- error: Optional error message`, RetrieveBrandfetchParamsSchema);
    }
    async execute(parameters) {
        const parseResult = safeParseToolParams(RetrieveBrandfetchParamsSchema, parameters);
        if (!parseResult.success) {
            throw new Error(`Invalid parameters: ${parseResult.error}`);
        }
        const params = parseResult.data;
        const assetPrefix = params.assetType === "logo" ? "logo" : "color";
        console.log(`[RetrieveBrandfetchTool] Retrieving ${params.assetType} from ${params.companyDomain}`);
        // Validate API keys
        if (!BRANDFETCH_API_KEY) {
            return {
                success: false,
                requirementId: params.requirementId,
                assets: [],
                error: 'Brandfetch API key not configured. Set BRANDFETCH_API_KEY in config.ts',
            };
        }
        if (params.logoOnly && params.assetType === 'logo' && !BRANDFETCH_CUSTOMER_ID) {
            return {
                success: false,
                requirementId: params.requirementId,
                assets: [],
                error: 'Brandfetch Customer ID not configured. Set BRANDFETCH_CUSTOMER_ID in config.ts',
            };
        }
        try {
            const contextResolver = ContextResolver.getInstance();
            // Fetch brand details (needed for both logos and colors)
            const brandData = await fetchBrandDetails(params.companyDomain, BRANDFETCH_API_KEY);
            if (!brandData) {
                return {
                    success: false,
                    requirementId: params.requirementId,
                    assets: [],
                    error: `Brand not found: ${params.companyDomain}`,
                };
            }
            if (params.assetType === 'logo') {
                // Extract all available variants and fetch each one
                const variants = extractAvailableVariants(brandData);
                console.log(`[RetrieveBrandfetchTool] Found ${variants.length} logo variants: ${variants.join(', ')}`);
                const assets = [];
                for (const variant of variants) {
                    try {
                        const result = await this.retrieveSingleLogo(params, brandData, variant, contextResolver, assetPrefix);
                        if (result) {
                            assets.push(result);
                        }
                    }
                    catch (error) {
                        console.warn(`[RetrieveBrandfetchTool] Failed to fetch variant ${variant}:`, error);
                        // Continue with other variants
                    }
                }
                if (assets.length === 0) {
                    return {
                        success: false,
                        requirementId: params.requirementId,
                        assets: [],
                        error: `Failed to retrieve any logo variants for ${params.companyDomain}`,
                    };
                }
                return {
                    success: true,
                    requirementId: params.requirementId,
                    assets,
                    _imageIds: assets.map(a => a.assetId), // Extract image IDs for injection
                };
            }
            else {
                // Brand colors
                const colorAsset = await this.retrieveBrandColors(params, brandData, contextResolver, assetPrefix);
                return {
                    success: colorAsset.success,
                    requirementId: params.requirementId,
                    assets: colorAsset.success ? [{
                            assetId: colorAsset.assetId,
                            note: colorAsset.note,
                            metadata: colorAsset.metadata,
                            alreadyExists: colorAsset.alreadyExists,
                        }] : [],
                    error: colorAsset.error,
                };
            }
        }
        catch (error) {
            // Handle rate limit errors with specific messaging
            if (error instanceof RateLimitError) {
                console.error("[RetrieveBrandfetchTool] ⚠️ Rate limit:", error.message);
                return {
                    success: false,
                    requirementId: params.requirementId,
                    assets: [],
                    error: `⚠️ Rate limit exceeded: ${error.message}`,
                };
            }
            console.error("[RetrieveBrandfetchTool] ❌ Error:", error);
            return {
                success: false,
                requirementId: params.requirementId,
                assets: [],
                error: `Retrieval failed: ${error instanceof Error ? error.message : 'Unknown error'}`,
            };
        }
    }
    /**
     * Retrieve a single logo variant.
     * Returns AssetInfo if successful, null if failed.
     */
    async retrieveSingleLogo(params, brandData, variant, contextResolver, assetPrefix) {
        // Create modified params with the specific variant
        const variantParams = { ...params, logoVariant: variant };
        // Call existing retrieveLogo method
        const result = await this.retrieveLogo(variantParams, brandData, contextResolver, assetPrefix);
        // Convert old response format to new AssetInfo format
        if (result.success) {
            return {
                assetId: result.assetId,
                note: result.note,
                metadata: result.metadata,
                alreadyExists: result.alreadyExists,
            };
        }
        return null;
    }
    async retrieveLogo(params, brandData, contextResolver, assetPrefix) {
        const variant = params.logoVariant || 'icon';
        const availableVariants = brandData ? extractAvailableVariants(brandData) : [];
        const hasDark = availableVariants.some((v) => v.startsWith('dark_'));
        const hasLight = availableVariants.some((v) => v.startsWith('light_'));
        const includeTone = hasDark && hasLight;
        let logoInfo = null;
        let downloaded = null;
        // Use Logo API if logoOnly is true
        if (params.logoOnly) {
            console.log(`[RetrieveBrandfetchTool] Using Logo API (logo_only mode) for ${params.companyDomain}`);
            // Logo API uses customerId (not API key) in the c parameter
            // Domain comes from searchBrandfetchTool (e.g., "apple.com")
            const logoApiUrl = buildLogoApiUrl(params.companyDomain, BRANDFETCH_CUSTOMER_ID, variant);
            console.log(`[RetrieveBrandfetchTool] Logo API URL: ${logoApiUrl}`);
            downloaded = await downloadImageAsBase64(logoApiUrl);
            if (!downloaded) {
                return {
                    success: false,
                    assetId: '',
                    requirementId: params.requirementId,
                    note: '',
                    metadata: { type: 'logo', source: 'brandfetch', dimensions: { width: 0, height: 0 }, format: 'png', shape: 'square', backgroundRemoved: false },
                    alreadyExists: false,
                    error: `Failed to download logo from Logo API for ${params.companyDomain}`,
                };
            }
            // Logo API format detection: icons may be SVG, logos are typically PNG
            // Check MIME type first, then check base64 content
            let isSvg = downloaded.mimeType.includes('svg');
            if (!isSvg && variant === 'icon') {
                // Check if base64 content is SVG (base64-encoded SVG starts with "PHN2Zy" or decode and check)
                try {
                    const decoded = atob(downloaded.base64.substring(0, Math.min(100, downloaded.base64.length)));
                    isSvg = decoded.trim().startsWith('<svg');
                }
                catch {
                    // If decoding fails, assume PNG
                    isSvg = false;
                }
            }
            logoInfo = {
                url: logoApiUrl,
                format: isSvg ? 'svg' : 'png',
                width: undefined,
                height: undefined,
            };
        }
        else {
            // Use Brand API 
            logoInfo = findLogoForVariant(brandData, variant);
            if (!logoInfo) {
                return {
                    success: false,
                    assetId: '',
                    requirementId: params.requirementId,
                    note: '',
                    metadata: { type: 'logo', source: 'brandfetch', dimensions: { width: 0, height: 0 }, format: 'png', shape: 'square', backgroundRemoved: false },
                    alreadyExists: false,
                    error: `No ${variant} logo found for ${params.companyDomain}`,
                };
            }
            console.log(`[RetrieveBrandfetchTool] Downloading ${logoInfo.format} from ${logoInfo.url}`);
            downloaded = await downloadImageAsBase64(logoInfo.url);
            if (!downloaded) {
                return {
                    success: false,
                    assetId: '',
                    requirementId: params.requirementId,
                    note: '',
                    metadata: { type: 'logo', source: 'brandfetch', dimensions: { width: 0, height: 0 }, format: 'png', shape: 'square', backgroundRemoved: false },
                    alreadyExists: false,
                    error: `Failed to download logo from ${logoInfo.url}`,
                };
            }
        }
        // Check deduplication
        const contentHash = simpleHash(downloaded.base64);
        const existingId = findExistingAsset(contentHash);
        if (existingId) {
            console.log(`[RetrieveBrandfetchTool] ✅ Asset already exists: ${existingId}`);
            const existing = contextResolver.resolve(existingId);
            const metadata = existing?.content?.metadata || {
                type: 'logo',
                source: 'brandfetch',
                dimensions: { width: logoInfo.width || 512, height: logoInfo.height || 512 },
                format: logoInfo.format,
                shape: 'square',
                backgroundRemoved: true,
            };
            return {
                success: true,
                assetId: existingId,
                requirementId: params.requirementId,
                note: generateAssetDescription(params.companyDomain, 'logo', logoInfo.format, metadata.shape, undefined, variant, includeTone),
                metadata,
                alreadyExists: true,
            };
        }
        const assetId = generateId(assetPrefix);
        // Convert (if SVG) and resize image if needed for Anthropic's vision API
        const resized = await resizeImageForVision(downloaded.base64, downloaded.mimeType);
        if (resized.wasConverted) {
            console.log(`[RetrieveBrandfetchTool] SVG converted to PNG for vision model compatibility`);
        }
        if (resized.wasResized) {
            console.log(`[RetrieveBrandfetchTool] Image resized for vision model compatibility`);
        }
        let paletteInfo = { palette: [], backgroundColor: 'transparent' };
        try {
            paletteInfo = await extractPaletteFromImage(resized.base64, resized.mimeType);
        }
        catch (error) {
            console.warn('[RetrieveBrandfetchTool] Palette extraction failed', error);
        }
        // Detect dimensions from the CONVERTED/PROCESSED image (after SVG→PNG conversion)
        let dimensions;
        if (resized.wasConverted || resized.mimeType.includes('png') || resized.mimeType.includes('jpeg') || resized.mimeType.includes('jpg') || resized.mimeType.includes('webp')) {
            // Detect from raster image (PNG/JPEG/WEBP)
            dimensions = getImageDimensions(resized.base64) || { width: logoInfo.width || 512, height: logoInfo.height || 512 };
        }
        else if (logoInfo.format === 'svg' && !resized.wasConverted) {
            // Still SVG, try to parse dimensions
            const svgContent = atob(resized.base64);
            dimensions = getSvgDimensions(svgContent) || { width: logoInfo.width || 512, height: logoInfo.height || 512 };
        }
        else {
            // Fallback to API dimensions or 512x512
            dimensions = { width: logoInfo.width || 512, height: logoInfo.height || 512 };
        }
        const shape = getShapeFromDimensions(dimensions.width, dimensions.height);
        // Store image (using potentially converted/resized version)
        contextResolver.storeImage(assetId, resized.base64, resized.mimeType, {
            source: 'brandfetch',
            companyDomain: params.companyDomain,
            variant,
            dimensions,
            originalFormat: downloaded.mimeType,
            wasConverted: resized.wasConverted,
            wasResized: resized.wasResized,
        });
        const metadata = {
            type: 'logo',
            source: 'brandfetch',
            dimensions,
            format: logoInfo.format,
            shape,
            backgroundRemoved: true,
            palette: paletteInfo.palette,
            backgroundColor: paletteInfo.backgroundColor,
        };
        contextResolver.storeWithId(assetId, {
            assetType: 'logo',
            companyDomain: params.companyDomain,
            variant,
            metadata,
            requirementId: params.requirementId,
            _imageId: assetId, // Include image ID so extractContextImageIds can find it
        });
        registerContentHash(contentHash, assetId);
        console.log(`[RetrieveBrandfetchTool] ✅ Retrieved logo: ${assetId} (${dimensions.width}x${dimensions.height} ${logoInfo.format})`);
        return {
            success: true,
            assetId,
            requirementId: params.requirementId,
            note: generateAssetDescription(params.companyDomain, 'logo', logoInfo.format, shape, undefined, variant, includeTone),
            metadata,
            alreadyExists: false,
        };
    }
    retrieveBrandColors(params, brandData, contextResolver, assetPrefix) {
        const colors = extractBrandColors(brandData);
        if (colors.length === 0) {
            return {
                success: false,
                assetId: '',
                requirementId: params.requirementId,
                note: '',
                metadata: { type: 'brand_colors', source: 'brandfetch', colors: [] },
                alreadyExists: false,
                error: `No brand colors found for ${params.companyDomain}`,
            };
        }
        // Check deduplication
        const contentHash = simpleHash(JSON.stringify(colors));
        const existingId = findExistingAsset(contentHash);
        if (existingId) {
            console.log(`[RetrieveBrandfetchTool] ✅ Colors already exist: ${existingId}`);
            return {
                success: true,
                assetId: existingId,
                requirementId: params.requirementId,
                note: generateAssetDescription(params.companyDomain, 'brand_colors', undefined, undefined, colors),
                metadata: { type: 'brand_colors', source: 'brandfetch', colors },
                alreadyExists: true,
            };
        }
        const assetId = generateId(assetPrefix);
        const metadata = { type: 'brand_colors', source: 'brandfetch', colors };
        contextResolver.storeWithId(assetId, {
            assetType: 'brand_colors',
            companyDomain: params.companyDomain,
            metadata,
            requirementId: params.requirementId,
        });
        registerContentHash(contentHash, assetId);
        console.log(`[RetrieveBrandfetchTool] ✅ Retrieved ${colors.length} brand colors: ${assetId}`);
        return {
            success: true,
            assetId,
            requirementId: params.requirementId,
            note: generateAssetDescription(params.companyDomain, 'brand_colors', undefined, undefined, colors),
            metadata,
            alreadyExists: false,
        };
    }
}
